import { Component } from "@angular/core";
import { GetRowIdParams, GridReadyEvent } from "@ag-grid-community/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Entity } from "breeze-client";
import _ from "lodash";
import { AgFns, GridOptionsExt } from "@/shared/ag-fns";
import { DialogBaseComponent, DialogParams } from "@/shared/dialog-base.component";
import { DomainService } from "@/shared/domain.service";
import { ActiveStatusEnum } from "@/model/entities/entity-model";
import { StagedFeedApiService } from "./staged-feed-api.service";
import { RetireProductDTO, RetireStyleDTO, RetireVerdict, RetirementScopeDTO } from "./staged-feed.models";

export interface BulkRetireDialogParams extends DialogParams {
  manufacturerId: number;
  manufacturerName: string;
  /** Which tab opens first. */
  initialTab?: "styles" | "products";

  /**
   * Open on ONE style. Narrowing only, and it narrows the Styles tab alone. The Products tab is
   * about colors and sizes a style no longer makes, which is a different question from retiring
   * the style. The verdict still governs: a style that needs a different fix arrives greyed.
   */
  focusProductTypeId?: number;
}

export interface BulkRetireResult {
  retiredStyles: number;
  retiredProducts: number;
}

type RetireRow = RetireStyleDTO | RetireProductDTO;

/**
 * Retires what the manufacturer feed does not answer for, in bulk. Retire means Phaseout
 * (gatekeeper L4): the row stops selling and its open work finishes.
 *
 * Two findings can only be fixed by taking something out of the catalogue: a feed style the feed
 * never returns or rejects outright, and a product whose color and size correspond to no part the
 * manufacturer publishes. Opening each one by hand is, for SanMar, hundreds of trips. Custom
 * styles (gatekeeper F3) are never listed: the feed says nothing about them.
 *
 * Retiring means ActiveStatusId = Phaseout, and the save goes through Breeze rather than a server
 * endpoint, because only a Breeze save writes the ProxSubmission row that stops Proximity
 * offering the thing. Retiring a style changes the style alone: a product's status is the worst of
 * its style's and its own, so its products stop selling with it, and the drain list stays one row
 * per style.
 *
 * Phaseout is always allowed, so open orders, purchase orders and stock no longer hold a row back.
 * They finish, and the counts on each row say what has to drain. When it has drained, the Phaseout
 * Drain page (References) offers Inactive. Before L4 retire set Inactive, which would strand that
 * work, so those rows were blocked.
 *
 * Saves once per style rather than once overall, same as the bulk color change: a failure then
 * costs one style instead of the run, and the progress line means something while hundreds move.
 */
@Component({
  templateUrl: "./bulk-retire-dialog.component.html",
  standalone: false,
})
export class BulkRetireDialogComponent extends DialogBaseComponent<BulkRetireDialogParams> {
  styleGridOptions: GridOptionsExt;
  productGridOptions: GridOptionsExt;

  scope: RetirementScopeDTO | null = null;
  activeTab = 0;

  isScanning = false;
  isApplying = false;
  errorMessage = "";

  progressDone = 0;
  progressTotal = 0;
  retiredStyles = 0;
  retiredProducts = 0;
  failed: string[] = [];

  constructor(
    public config: DynamicDialogConfig<BulkRetireDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    private api: StagedFeedApiService
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, { header: "Retire Unavailable Products", width: "1440px", height: "1130px" });

    this.activeTab = this.params.initialTab === "products" ? 1 : 0;

    this.styleGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onStyleGridReady,
      getRowId: (p: GetRowIdParams) => String((p.data as RetireStyleDTO).ProductTypeId),
      cellSelection: false,
      getRowClass: (p: any) => (BulkRetireDialogComponent.canRetire(p.data) ? "" : "rs-already-processed"),
    });

    this.productGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onProductGridReady,
      getRowId: (p: GetRowIdParams) => String((p.data as RetireProductDTO).ProductId),
      cellSelection: false,
      getRowClass: (p: any) => (BulkRetireDialogComponent.canRetire(p.data) ? "" : "rs-already-processed"),
    });

    this.prepare();
  }

  /**
   * isPageReady goes true BEFORE the scan, not after.
   *
   * The shared dialog shell hides everything until isPageReady and shows a bare unlabelled
   * spinner in the corner, which on a dialog this size reads as nothing happening at all. The
   * scan is slow for a manufacturer the size of SanMar — it walks every product, every staged
   * part, and all the live stock and order usage — so the wait needs to be visible and say what
   * it is waiting for.
   */
  private async prepare() {
    this.isScanning = true;
    this.errorMessage = "";
    this.isPageReady = true;
    try {
      this.scope = await this.api.getRetirementCandidates(this.params.manufacturerId);

      // Opened on one style. Dropped if that style is not a candidate - the feed answers for it,
      // so there is nothing here to retire - and an empty grid would read as a broken screen
      // rather than as an answer.
      const focused = this.params.focusProductTypeId == null
        ? null
        : this.scope.Styles.filter(x => x.ProductTypeId === this.params.focusProductTypeId);
      if (focused?.length) this.scope.Styles = focused;

      this.styleGridOptions.apiX?.setGridOption("rowData", this.scope.Styles);
      this.productGridOptions.apiX?.setGridOption("rowData", this.scope.Products);
    } catch (e: any) {
      this.errorMessage = e?.error?.Message ?? e?.message ?? "The system could not read the candidates.";
    }
    this.isScanning = false;
  }

  static canRetire(row: RetireRow) {
    return row?.Verdict === "Retire";
  }

  /** Active or Hold: still sold. Phaseout and Inactive are out of the catalog. */
  static isLive(status: ActiveStatusEnum) {
    return status === ActiveStatusEnum.Active || status === ActiveStatusEnum.Hold;
  }

  static hasOpenWork(row: RetireRow) {
    return !!row && row.OpenJoDetailCount + row.OpenPoQty + row.InInventoryQty > 0;
  }

  static resultLabel(row: RetireRow): string {
    if (row?.Verdict === "Retired") return "On Phaseout";
    if (row?.Verdict === "Retire") {
      return BulkRetireDialogComponent.hasOpenWork(row) ? "Ready. The open work finishes first." : "Ready";
    }
    return BulkRetireDialogComponent.verdictLabel(row?.Verdict);
  }

  static verdictLabel(v: RetireVerdict): string {
    switch (v) {
      case "Retire": return "Ready";
      case "FixStyleCode": return "Correct the SKU";
      case "ReadAgain": return "Read the style again";
      default: return v ?? "";
    }
  }

  static reasonLabel(r: string): string {
    switch (r) {
      case "NeverRead": return "The feed has never returned it";
      case "Rejected": return "The manufacturer does not make it";
      case "WrongStyleCode": return "The feed lists it under another code";
      case "ReadFailed": return "The system could not read it";
      default: return r ?? "";
    }
  }

  // ---------- counts the header reads from ----------

  get styles() { return this.scope?.Styles ?? []; }
  get products() { return this.scope?.Products ?? []; }

  get retirableStyles() { return this.styles.filter(s => BulkRetireDialogComponent.canRetire(s)); }
  get retirableProducts() { return this.products.filter(p => BulkRetireDialogComponent.canRetire(p)); }

  get blockedStyles() { return this.styles.filter(s => !BulkRetireDialogComponent.canRetire(s)); }
  get blockedProducts() { return this.products.filter(p => !BulkRetireDialogComponent.canRetire(p)); }

  get isStyleTab() { return this.activeTab === 0; }

  private get activeGrid() {
    return this.isStyleTab ? this.styleGridOptions : this.productGridOptions;
  }

  /** Every ticked row on the tab in view, blocked rows excluded even if one slipped through. */
  private checkedRows<T extends RetireRow>(): T[] {
    const rows: T[] = [];
    this.activeGrid.apiX?.forEachNode(node => {
      const d = node.data as any;
      if (d?._isRetireChecked && BulkRetireDialogComponent.canRetire(d)) rows.push(d);
    });
    return rows;
  }

  get canApply() {
    const available = this.isStyleTab ? this.retirableStyles.length : this.retirableProducts.length;
    return available > 0 && !this.isApplying && !this.isScanning;
  }

  /** The shortcut. One press arms every row with nothing in the way. */
  tickAllRetirable() {
    this.activeGrid.apiX?.forEachNode(node => {
      const d = node.data as any;
      if (BulkRetireDialogComponent.canRetire(d)) d._isRetireChecked = true;
    });
    this.activeGrid.apiX?.refreshCells({ force: true });
  }

  clearTicks() {
    this.activeGrid.apiX?.forEachNode(node => { (node.data as any)._isRetireChecked = false; });
    this.activeGrid.apiX?.refreshCells({ force: true });
  }

  onTabChange(e: any) {
    this.activeTab = e?.index ?? 0;
  }

  // ---------- apply ----------

  async onApply() {
    if (!this.canApply) return;
    return this.isStyleTab ? this.applyStyles() : this.applyProducts();
  }

  private async applyStyles() {
    const rows = this.checkedRows<RetireStyleDTO>();
    if (rows.length === 0) {
      await this.dialogService.showOkMessage("Nothing Selected", "Select at least one style to retire.");
      return;
    }

    const withWork = rows.filter(r => BulkRetireDialogComponent.hasOpenWork(r)).length;
    const ok = await this.dialogService.yesNoDialog(
      "Retire Styles",
      `The system puts ${rows.length} style(s) on Phaseout. No one can order their products.`
        + (withWork > 0
          ? ` ${withWork} of the styles have open orders, purchase orders or stock. That work finishes.`
          : "")
        + `<br/><br/>When the open work on a style is done, the style shows on the Phaseout Drain page`
        + ` (References). A product admin makes it Inactive there.`
        + `<br/><br/><b>Did you export the errors to Excel?</b> These styles leave the error list`
        + ` when you retire them. If you do not have the file, press Cancel and make it first.`
        + `<br/><br/>The change is sent to Proximity on the next push. Proximity stops offering these`
        + ` styles. Each style also makes a Trello card in the Discontinued Products list.`
        + `<br/><br/>Do you want to continue?`,
      "Ok",
      "Cancel"
    );
    if (!ok) return;

    this.startRun(rows.length);

    for (const row of rows) {
      try {
        await this.retireOneStyle(row);
        this.retiredStyles++;
        row.Verdict = "Retired";
      } catch (e: any) {
        this.uow.rollback();
        this.failed.push(row.Style);
        console.error(`Retire failed on style ${row.Style}`, e);
      }
      this.progressDone++;
    }

    this.finishRun();
  }

  /** Puts the style on Phaseout. Its products keep their own status and stop selling with it. */
  private async retireOneStyle(row: RetireStyleDTO) {
    const pt = await this.dbQueryService.getProductType(row.ProductTypeId);
    if (pt == null) throw new Error(`The system could not find product type ${row.ProductTypeId}.`);
    if (!BulkRetireDialogComponent.isLive(pt.activeStatusId)) return;

    pt.activeStatusId = ActiveStatusEnum.Phaseout;
    const saved = await this.dbSaveService.saveSelectedChanges([pt]);
    if (!saved) {
      this.uow.rollback();
      throw new Error(`The save was refused on style ${row.Style}.`);
    }
  }

  private async applyProducts() {
    const rows = this.checkedRows<RetireProductDTO>();
    if (rows.length === 0) {
      await this.dialogService.showOkMessage("Nothing Selected", "Select at least one product to retire.");
      return;
    }

    const byStyle = _.groupBy(rows, r => r.ProductTypeId);
    const styleCount = Object.keys(byStyle).length;

    // A style whose last live product goes out goes on Phaseout with it. Said here, before the
    // save, because retiring a style is a bigger act than retiring a color and nobody should
    // meet it as a surprise in the result line.
    const emptied = Object.keys(byStyle).filter(id => {
      const group = byStyle[id];
      return group.length >= (group[0].ActiveProductCount ?? 0);
    });

    const emptiedWarning = emptied.length === 0
      ? ` The styles stay active.`
      : `<br/><br/><b>This empties ${emptied.length} style(s) completely.</b> The system puts`
        + ` each of those styles on Phaseout as well, in the same save. A style that stays active with no`
        + ` products is a style Proximity still offers and cannot fill.`;

    const withWork = rows.filter(r => BulkRetireDialogComponent.hasOpenWork(r)).length;
    const ok = await this.dialogService.yesNoDialog(
      "Retire Products",
      `The system puts ${rows.length} product(s) on Phaseout, across ${styleCount} style(s).`
        + ` No one can order them.`
        + (withWork > 0
          ? ` ${withWork} of the products have open orders, purchase orders or stock. That work finishes.`
          : "")
        + emptiedWarning
        + `<br/><br/>When the open work on a product is done, the product shows on the Phaseout Drain`
        + ` page (References). A product admin makes it Inactive there.`
        + `<br/><br/>The change is sent to Proximity on the next push. Proximity stops offering`
        + ` these colors and sizes.`
        + `<br/><br/>Do you want to continue?`,
      "Ok",
      "Cancel"
    );
    if (!ok) return;

    this.startRun(styleCount);

    for (const productTypeId of Object.keys(byStyle)) {
      const group = byStyle[productTypeId];
      try {
        await this.retireProductsOnOneStyle(Number(productTypeId), group);
      } catch (e: any) {
        this.uow.rollback();
        this.failed.push(group[0].Style);
        console.error(`Retire failed on style ${group[0].Style}`, e);
      }
      this.progressDone++;
    }

    this.finishRun();
  }

  /**
   * Puts the selected products on Phaseout, and the style itself when no live product is left.
   *
   * The emptied test is made HERE rather than from the scan, against the products Breeze just
   * loaded. The scan is a snapshot: another operator can retire a product between the scan and
   * the save, and a style emptied by the two of you together is still an emptied style.
   *
   * Loaded through getProductType rather than getProductsForProductType because the ProductType
   * has to be in the same save — a style that went inactive while its products stayed live, or
   * the reverse, is worse than either end of it alone.
   */
  private async retireProductsOnOneStyle(productTypeId: number, rows: RetireProductDTO[]) {
    const pt = await this.dbQueryService.getProductType(productTypeId);
    if (pt == null) throw new Error(`The system could not find product type ${productTypeId}.`);

    const wanted = new Set(rows.map(r => r.ProductId));
    const touched: Entity[] = [];

    pt.products
      .filter(p => wanted.has(p.id) && BulkRetireDialogComponent.isLive(p.activeStatusId))
      .forEach(p => {
        p.activeStatusId = ActiveStatusEnum.Phaseout;
        touched.push(p);
      });

    if (touched.length === 0) return;
    const productsRetired = touched.length;

    const anyLeft = pt.products.some(p => BulkRetireDialogComponent.isLive(p.activeStatusId));
    const emptiesStyle = !anyLeft && BulkRetireDialogComponent.isLive(pt.activeStatusId);
    if (emptiesStyle) {
      pt.activeStatusId = ActiveStatusEnum.Phaseout;
      touched.push(pt);
    }

    const saved = await this.dbSaveService.saveSelectedChanges(touched);
    if (!saved) {
      this.uow.rollback();
      throw new Error(`The save was refused on style ${rows[0].Style}.`);
    }
    this.retiredProducts += productsRetired;
    if (emptiesStyle) this.retiredStyles++;
    rows.forEach(r => { r.Verdict = "Retired"; });
  }

  private startRun(total: number) {
    this.isApplying = true;
    this.errorMessage = "";
    this.progressTotal = total;
    this.progressDone = 0;
    this.retiredStyles = 0;
    this.retiredProducts = 0;
    this.failed = [];
  }

  private finishRun() {
    this.isApplying = false;
    // redrawRows, not refreshCells — the rows that just went out are restyled by getRowClass,
    // and refreshCells leaves the row class as it was.
    this.activeGrid.apiX?.redrawRows();

    if (this.failed.length > 0) {
      this.errorMessage = `The system could not put ${this.failed.length} style(s) on Phaseout:`
        + ` ${this.failed.slice(0, 10).join(", ")}.`;
    }
  }

  // ---------- grids ----------

  /** The tick column. A row that needs a different fix is not editable, so no one can arm it by accident. */
  private checkboxColDef() {
    return {
      ...AgFns.createCheckboxProps("", "_isRetireChecked", "retireCheckbox"),
      valueGetter: (p: any) => !!p.data?._isRetireChecked,
      valueSetter: (p: any) => {
        if (!p.data || !BulkRetireDialogComponent.canRetire(p.data)) return false;
        p.data._isRetireChecked = !!p.newValue;
        return true;
      },
      editable: (p: any) => BulkRetireDialogComponent.canRetire(p.data),
      sortable: false,
      filter: false,
      ...AgFns.fixWidth(50),
    };
  }

  private blockerColDefs() {
    return [
      {
        headerName: "Open Work",
        children: [
          { headerName: "Job Order Lines", field: "OpenJoDetailCount", type: "numericColumn", ...AgFns.fixWidth(130) },
          { headerName: "Quantity to Receive", field: "OpenPoQty", type: "numericColumn", ...AgFns.fixWidth(140) },
          { headerName: "In Stock", field: "InInventoryQty", type: "numericColumn", ...AgFns.fixWidth(100) },
        ],
      },
      {
        headerName: "Result",
        colId: "result",
        filter: "agSetColumnFilter",
        ...AgFns.flexWidth(230),
        valueGetter: (p: any) => BulkRetireDialogComponent.resultLabel(p.data),
      },
    ];
  }

  onStyleGridReady = (event: GridReadyEvent) => {
    const colDefs = [
      this.checkboxColDef(),
      { headerName: "Style", field: "Style", filter: "agTextColumnFilter", ...AgFns.flexWidth(130) },
      { headerName: "Description", field: "Description", filter: "agTextColumnFilter", ...AgFns.flexWidth(260) },
      {
        headerName: "Why",
        field: "Reason",
        filter: "agSetColumnFilter",
        ...AgFns.flexWidth(200),
        valueFormatter: (p: any) => BulkRetireDialogComponent.reasonLabel(p.value),
      },
      {
        headerName: "Products",
        colId: "productCount",
        type: "numericColumn",
        ...AgFns.fixWidth(110),
        valueGetter: (p: any) => (p.data as RetireStyleDTO)?.ProductIds?.length ?? 0,
      },
      ...this.blockerColDefs(),
    ];
    AgFns.initGrid(event, colDefs, [{ colId: "Style", sort: "asc" }]);
    if (this.scope) event.api.setGridOption("rowData", this.scope.Styles);
  };

  onProductGridReady = (event: GridReadyEvent) => {
    const colDefs = [
      this.checkboxColDef(),
      { headerName: "Style", field: "Style", filter: "agTextColumnFilter", ...AgFns.flexWidth(130) },
      { headerName: "Color", field: "ColorName", filter: "agTextColumnFilter", ...AgFns.flexWidth(200) },
      { headerName: "Size", field: "SizeName", filter: "agTextColumnFilter", ...AgFns.flexWidth(130) },
      {
        // Against the count of rows you tick for a style, this is what says whether the style
        // itself goes out. Twelve rows on a style of twelve is the whole style.
        headerName: "Live On Style",
        field: "ActiveProductCount",
        type: "numericColumn",
        ...AgFns.fixWidth(130),
      },
      ...this.blockerColDefs(),
    ];
    AgFns.initGrid(event, colDefs, [{ colId: "Style", sort: "asc" }]);
    if (this.scope) event.api.setGridOption("rowData", this.scope.Products);
  };

  onClose() {
    this.uow.rollback();
    this.dialogRef.close(
      this.retiredStyles > 0 || this.retiredProducts > 0
        ? ({ retiredStyles: this.retiredStyles, retiredProducts: this.retiredProducts } as BulkRetireResult)
        : null
    );
  }
}
